import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, RefreshCw, ArrowUpCircle, CheckCircle2, Clock, AlertTriangle,
  Link2, Scale, Settings2, History, BarChart3, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const MODULE_COLORS = {
  DCI: '#3B82F6', MCI: '#22C55E', QPT: '#F59E0B', WBS: '#143A6B',
};

function ContributionBar({ breakdown }) {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex rounded-full overflow-hidden h-5" style={{ backgroundColor: 'var(--border-color)' }}>
        {breakdown.map((b, i) => (
          <div
            key={b.linkId || i}
            className="h-full relative group transition-all"
            style={{
              width: `${b.weight}%`,
              backgroundColor: MODULE_COLORS[b.microModule] || '#6B7280',
              opacity: b.microProgressPct > 0 ? 1 : 0.3,
            }}
            title={`${b.microModule}: ${b.microActivityCode} — ${b.weight}% weight, ${b.microProgressPct}% progress`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {breakdown.map((b, i) => (
          <div key={b.linkId || i} className="flex items-center gap-1 text-[10px]">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: MODULE_COLORS[b.microModule] || '#6B7280' }} />
            <span style={{ color: 'var(--text-muted)' }}>{b.microModule}: {b.weight}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MacroDetailPanel({ projectId, macro, onClose }) {
  const [tab, setTab] = useState('links');
  const [breakdown, setBreakdown] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [config, setConfig] = useState({});

  const macroId = macro?.id;

  const fetchBreakdown = useCallback(async () => {
    if (!macroId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/schedule/macro/${macroId}/breakdown`);
      setBreakdown(data);
      setConfig(data.config || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, macroId]);

  const fetchHistory = useCallback(async () => {
    if (!macroId) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/schedule/rollup/${macroId}/history`);
      setHistory(data.history || []);
    } catch { /* ignore */ }
  }, [projectId, macroId]);

  useEffect(() => { fetchBreakdown(); fetchHistory(); }, [fetchBreakdown, fetchHistory]);

  const handleRollUp = async () => {
    setRolling(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/schedule/rollup/${macroId}`, {});
      toast.success(`Rolled up: ${data.previousProgress}% → ${data.newProgress}% (${data.delta > 0 ? '+' : ''}${data.delta}%)`);
      fetchBreakdown();
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Roll-up failed');
    }
    setRolling(false);
  };

  const handleRebalance = async () => {
    try {
      await api.put(`/v1/projects/${projectId}/schedule/links/rebalance-weights/${macroId}`);
      toast.success('Weights rebalanced equally');
      fetchBreakdown();
    } catch { toast.error('Rebalance failed'); }
  };

  const handleConfigChange = async (field, value) => {
    try {
      await api.put(`/v1/projects/${projectId}/schedule/macro/${macroId}/config`, { [field]: value });
      setConfig(prev => ({ ...prev, [field]: value }));
      toast.success('Config updated');
    } catch { toast.error('Failed to update config'); }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await api.delete(`/v1/projects/${projectId}/schedule/links/${linkId}`);
      toast.success('Link removed');
      fetchBreakdown();
    } catch { toast.error('Failed to remove link'); }
  };

  const bd = breakdown?.breakdown || [];
  const rolledUp = breakdown?.rolledUpProgress;
  const status = breakdown?.status || 'UNLINKED';

  const tabs = [
    { id: 'links', label: 'Linked Activities', icon: Link2 },
    { id: 'config', label: 'Configuration', icon: Settings2 },
    { id: 'history', label: 'Audit Trail', icon: History },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
        data-testid="macro-detail-panel"
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg" style={{ background: '#143A6B15' }}>
              <BarChart3 className="w-4 h-4" style={{ color: '#143A6B' }} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{macro?.name || 'Macro Activity'}</div>
              <div className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{macro?.wbsCode || macro?.id}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {rolledUp != null && (
              <span className="text-lg font-bold font-mono" style={{ color: '#143A6B' }} data-testid="macro-rolled-up-pct">{rolledUp}%</span>
            )}
            <Button size="sm" onClick={handleRollUp} disabled={rolling || bd.length === 0} data-testid="execute-rollup-btn">
              {rolling ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />}
              Roll Up Now
            </Button>
            <button onClick={onClose} style={{ color: 'var(--text-muted)' }} data-testid="close-macro-panel">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Contribution Bar */}
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <ContributionBar breakdown={bd} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 pt-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors"
                style={{
                  color: tab === t.id ? '#143A6B' : 'var(--text-muted)',
                  borderBottom: tab === t.id ? '2px solid #143A6B' : '2px solid transparent',
                }}
                data-testid={`macro-tab-${t.id}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : tab === 'links' ? (
            <div className="space-y-2" data-testid="linked-activities-list">
              {bd.length === 0 ? (
                <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>No micro activities linked yet</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--hover-bg)' }}>
                      <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Module</th>
                      <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Code</th>
                      <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Activity</th>
                      <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Weight</th>
                      <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Progress</th>
                      <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Contribution</th>
                      <th className="text-center p-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Status</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bd.map((b, i) => (
                      <tr key={b.linkId || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td className="p-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                            style={{ backgroundColor: `${MODULE_COLORS[b.microModule]}15`, color: MODULE_COLORS[b.microModule] }}>
                            {b.microModule}
                          </span>
                        </td>
                        <td className="p-2 font-mono" style={{ color: 'var(--text-primary)' }}>{b.microActivityCode || '—'}</td>
                        <td className="p-2 max-w-[180px] truncate" style={{ color: 'var(--text-primary)' }}>{b.microActivityName || '—'}</td>
                        <td className="p-2 text-center font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{b.weight}%</td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                              <div className="h-full rounded-full" style={{ width: `${Math.min(100, b.microProgressPct)}%`, backgroundColor: MODULE_COLORS[b.microModule] }} />
                            </div>
                            <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{b.microProgressPct}%</span>
                          </div>
                        </td>
                        <td className="p-2 text-center font-mono" style={{ color: '#143A6B' }}>{b.weightedContribution}%</td>
                        <td className="p-2 text-center">
                          {b.isRolledUp ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mx-auto" style={{ color: '#22C55E' }} />
                          ) : (
                            <Clock className="w-3.5 h-3.5 mx-auto" style={{ color: '#F59E0B' }} />
                          )}
                        </td>
                        <td className="p-2">
                          <button onClick={() => handleDeleteLink(b.linkId)} className="text-[10px] px-1.5 py-0.5 rounded hover:opacity-80"
                            style={{ color: '#EF4444', backgroundColor: '#EF444410' }} data-testid={`unlink-${b.linkId}`}>
                            Unlink
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {bd.length > 0 && (
                <div className="flex items-center justify-between pt-2">
                  <div className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>
                    Total weight: <span className="font-bold" style={{ color: Math.abs((breakdown?.totalWeight || 0) - 100) < 0.5 ? '#22C55E' : '#EF4444' }}>
                      {breakdown?.totalWeight}%
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleRebalance} data-testid="rebalance-weights-btn">
                    <Scale className="w-3 h-3 mr-1" /> Rebalance Weights
                  </Button>
                </div>
              )}
            </div>
          ) : tab === 'config' ? (
            <div className="space-y-4" data-testid="rollup-config-panel">
              <div>
                <label className="text-[10px] uppercase font-semibold block mb-1" style={{ color: 'var(--text-disabled)' }}>Roll-Up Method</label>
                <select value={config.rollUpMethod || 'WEIGHTED_AVERAGE'} onChange={e => handleConfigChange('rollUpMethod', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-sm" data-testid="rollup-method-select"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="WEIGHTED_AVERAGE">Weighted Average</option>
                  <option value="MINIMUM">Minimum</option>
                  <option value="MAXIMUM">Maximum</option>
                  <option value="WEIGHTED_MILESTONE">Weighted Milestone</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold block mb-1" style={{ color: 'var(--text-disabled)' }}>Progress Source</label>
                <select value={config.activeProgressSource || 'ROLL_UP'} onChange={e => handleConfigChange('activeProgressSource', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-sm" data-testid="progress-source-select"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="ROLL_UP">Roll-Up (Bottom-Up)</option>
                  <option value="MANUAL">Manual Override</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.autoRollUpOnMicroSave || false}
                    onChange={e => handleConfigChange('autoRollUpOnMicroSave', e.target.checked)}
                    className="rounded" data-testid="auto-rollup-toggle" />
                  <span className="text-xs" style={{ color: 'var(--text-primary)' }}>Auto roll-up on micro save</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="rollup-history-list">
              {history.length === 0 ? (
                <div className="text-center py-8 text-xs" style={{ color: 'var(--text-muted)' }}>No roll-up history yet</div>
              ) : (
                history.slice(0, 20).map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--hover-bg)' }}>
                    <div className="flex-shrink-0">
                      <History className="w-3.5 h-3.5" style={{ color: 'var(--text-disabled)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span style={{ color: 'var(--text-primary)' }}>
                        <span className="font-mono px-1 rounded" style={{ backgroundColor: `${MODULE_COLORS[h.microModule]}15`, color: MODULE_COLORS[h.microModule] }}>
                          {h.microModule}
                        </span>
                        {' '}{h.microActivityCode}
                      </span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {' '}— {h.previousValue}% → {h.newMacroProgress}%
                      </span>
                    </div>
                    <div className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-disabled)' }}>
                      {h.rolledUpAt ? new Date(h.rolledUpAt).toLocaleString() : ''}
                    </div>
                    <span className="text-[9px] px-1.5 py-0.5 rounded" style={{
                      backgroundColor: h.triggerSource === 'MANUAL' ? '#3B82F615' : '#22C55E15',
                      color: h.triggerSource === 'MANUAL' ? '#3B82F6' : '#22C55E',
                    }}>{h.triggerSource}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
